import { Role } from '@bioliberty/core';
import { Info } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { TagPicker } from 'rsuite';
import { z } from 'zod';
import { UpdateUserParams, useUpdateUserMutation } from '../../api';
import { useAuthentication } from '../../auth';
import { Button } from '../../components/Button';
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/Dialog';
import { Icon } from '../../components/Icon';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/Input/Select';
import { ErrorMessage, InputLabel, TextInput } from '../../components/Input/TextInput';
import { Switch } from '../../components/Switch';
import { nameRegExp } from '../../constants';
import { useActiveSubtenants } from '../../hooks/useActiveSubtenants';
import { useToaster } from '../../hooks/useToaster';
import { useValidator } from '../../hooks/useValidator';
import { updateUser as updateAdminViewUser } from '../../stores/slices/admin-view';
import { isAdmin, isSuperAdmin } from '../../util/auth';
import { getRoleLabel } from '../utils';

type EditUserModalProps = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  subtenants?: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  enabled?: boolean;
};

export const EditUserModal = ({
  id,
  email,
  firstName,
  lastName,
  roles,
  subtenants,
  enabled,
  open,
  onOpenChange
}: EditUserModalProps) => {
  const currentUser = useAuthentication();
  const activeSubtenants = useActiveSubtenants();
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();
  const dispatch = useDispatch();
  const toast = useToaster();

  const updatableSubtenants = useMemo(() => {
    return subtenants?.filter((s) => currentUser.subtenants?.some(({ name }) => name === s));
  }, [currentUser, subtenants]);

  const [userUpdate, setUserUpdate] = useState<UpdateUserParams>({
    id,
    firstName,
    lastName,
    roles,
    enabled,
    subtenants: updatableSubtenants
  });

  const clinicOptions = useMemo(() => {
    const activeOptions = activeSubtenants?.map(({ name, label }) => ({ value: name, label })) ?? [];

    const inactiveSelected =
      currentUser.subtenants
        ?.filter(({ inactive, name }) => inactive && updatableSubtenants?.includes(name))
        .map(({ name, label }) => ({ value: name, label })) ?? [];

    return [...activeOptions, ...inactiveSelected];
  }, [activeSubtenants, currentUser.subtenants, updatableSubtenants]);

  const { validate, errors } = useValidator(
    z
      .object({
        firstName: z.string().regex(nameRegExp, 'Please enter a valid name').min(1, 'A first name is required'),
        lastName: z.string().regex(nameRegExp, 'Please enter a valid name').min(1, 'A last name is required'),
        roles: z.array(z.enum(Role, { message: 'Please select a valid role' })).min(1, 'A role is required'),
        subtenants: z.array(z.string()).optional()
      })
      .refine(({ roles, subtenants }) => !clinicOptions.length || !!subtenants?.length || isSuperAdmin({ roles }), {
        message: 'At least one clinic assignment is required',
        path: ['subtenants']
      })
  );

  const isCurrentUser = useMemo(() => currentUser.id === id, [currentUser, id]);

  const shouldShowClinicAssignments = useMemo(() => {
    return !!clinicOptions.length;
  }, [clinicOptions]);

  const onChange = useCallback(
    (property: string, value: string | string[] | boolean) => {
      setUserUpdate({ ...userUpdate, [property]: value });
    },
    [userUpdate]
  );

  const submit = useCallback(async () => {
    if (!validate(userUpdate)) {
      return;
    }

    try {
      await updateUser({
        ...userUpdate,
        enabled: userUpdate.enabled !== enabled ? userUpdate.enabled : undefined
      }).unwrap();

      dispatch(updateAdminViewUser({ id: userUpdate.id, props: userUpdate }));
      toast.success('User updated successfully.');
      onOpenChange(false);
    } catch {
      toast.error('Failed to update user. Please try again.');
    }
  }, [validate, updateUser, userUpdate, enabled, dispatch, toast, onOpenChange]);

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        onOpenChange(open);
        setUserUpdate({
          id,
          firstName,
          lastName,
          roles,
          enabled,
          subtenants: updatableSubtenants
        });
      }}
    >
      <DialogContent
        className="sm:max-w-[800px]"
        data-testid="edit-user-modal"
        // Necessary workaround to fix scrolling within the clinic picker:
        // https://github.com/radix-ui/primitives/issues/1159
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>{`Edit ${firstName} ${lastName}`}</DialogTitle>
        </DialogHeader>

        <div className="mb-5 flex flex-col justify-between">
          <div className="text-xl">Personal Details</div>
          <div className="flex gap-5">
            <div className="mt-2.5 flex-1">
              <TextInput
                initialValue={email}
                label="Email address*"
                onChange={(value) => onChange('email', value)}
                disabled={true}
              />
            </div>
            <div className="mt-2.5 flex-1">
              <InputLabel htmlFor="input-field">Role*</InputLabel>
              <Select
                onValueChange={(value) => {
                  setUserUpdate({
                    ...userUpdate,
                    roles: [value],
                    subtenants: value === Role.SuperAdmin ? undefined : userUpdate.subtenants
                  });
                }}
                value={userUpdate.roles.length > 0 ? userUpdate.roles[0] : Role.Clinician}
                disabled={
                  /* The current user cannot remove the admin role from themselves  */
                  (isCurrentUser && (isAdmin(userUpdate) || isSuperAdmin(userUpdate))) ||
                  /* Only super admins can change the role of other super admins  */
                  (isSuperAdmin(userUpdate) && !isSuperAdmin(currentUser))
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {(isSuperAdmin(userUpdate) || isSuperAdmin(currentUser)) && (
                    <SelectItem value={Role.SuperAdmin}>{getRoleLabel(Role.SuperAdmin)}</SelectItem>
                  )}
                  <SelectItem value={Role.Admin}>{getRoleLabel(Role.Admin)}</SelectItem>
                  <SelectItem value={Role.Clinician}>{getRoleLabel(Role.Clinician)}</SelectItem>
                </SelectContent>
              </Select>
              <ErrorMessage>{errors.role}</ErrorMessage>
            </div>
          </div>
          <div className="flex gap-5">
            <div className="mt-2.5 flex-1">
              <TextInput
                initialValue={userUpdate.firstName}
                label="First name*"
                onChange={(value) => onChange('firstName', value)}
                errorMessage={errors.firstName}
                testId="first-name-input"
              />
            </div>
            <div className="mt-2.5 flex-1">
              <TextInput
                initialValue={userUpdate.lastName}
                label="Last name*"
                onChange={(value) => onChange('lastName', value)}
                errorMessage={errors.lastName}
                testId="last-name-input"
              />
            </div>
          </div>

          {shouldShowClinicAssignments && (
            <div className="mt-2.5">
              <InputLabel>Clinic Assignments*</InputLabel>
              {!isSuperAdmin(userUpdate) && (
                <TagPicker
                  data={clinicOptions}
                  value={userUpdate.subtenants}
                  onChange={(value) => onChange('subtenants', value)}
                  placeholder="Select one or more clinics"
                  className="dark w-full"
                  menuStyle={{ overflow: 'auto' }}
                />
              )}
              {isSuperAdmin(userUpdate) && (
                <div className="flex min-h-[36px] items-center">
                  <Icon type={Info} className="me-2 inline text-xs" />
                  This user role has access to all clinics.
                </div>
              )}
              {errors.subtenants && <ErrorMessage>{errors.subtenants}</ErrorMessage>}
            </div>
          )}

          {!isCurrentUser && (
            <>
              <div className="mt-5 mb-2.5 text-xl">Authentication</div>
              <div className="flex flex-col gap-5">
                <div className="text-sm">A disabled user will not be able to sign in.</div>
                <Switch
                  checked={!!userUpdate.enabled}
                  onChange={(value) => onChange('enabled', value)}
                  checkedChildren="Enabled"
                  unCheckedChildren="Disabled"
                  size="lg"
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="accent"
            onClick={() => submit()}
            disabled={isUpdating}
            isLoading={isUpdating}
            data-testid="edit-user-modal-save-btn"
          >
            Save
          </Button>
          <DialogClose asChild>
            <Button variant="transparent">Cancel</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
